/**
 * Contexto de Suscripción
 *
 * Maneja el estado de suscripción del usuario en toda la aplicación
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  getSubscriptions,
  createSubscription as createSubscriptionAPI,
  getSubscriptionUsage,
} from '../services/api';
import { useAuth } from './AuthContext';
import { storage } from '../utils/storage';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription debe ser usado dentro de un SubscriptionProvider');
  }
  return context;
};

// Keys para localStorage
const SUBSCRIPTION_ID_KEY = 'subscriptionId';

export const SubscriptionProvider = ({ children }) => {
  const { user, organization } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false); // Indica si ya se verificó al menos una vez
  const [error, setError] = useState(null);

  // Verificar si hay una suscripción activa al cargar la app o cuando cambie la organización
  useEffect(() => {
    if (user && organization) {
      checkSubscription();
    } else {
      setLoading(false);
      setSubscription(null);
      setUsage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, organization]);

  const checkSubscription = async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    try {
      setLoading(true);
      setError(null);
      const response = await getSubscriptions({
        organizationId: organization.organizationId,
      });

      const activeSubs =
        response.subscriptions?.filter((sub) => {
          return sub.status === 'active' || sub.status === 'trial' || sub.status === 'canceling';
        }) || [];

      if (activeSubs.length > 0) {
        // Priorizar: trial/active con plan != free > active con plan != free > cualquier active
        const sortedSubs = [...activeSubs].sort((a, b) => {
          if (a.status === 'trial' && b.status !== 'trial') return -1;
          if (b.status === 'trial' && a.status !== 'trial') return 1;
          if (a.planId !== 'free' && b.planId === 'free') return -1;
          if (b.planId !== 'free' && a.planId === 'free') return 1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        const activeSub = sortedSubs[0];
        setSubscription(activeSub);

        // Guardar subscriptionId en localStorage
        if (activeSub.subscriptionId) {
          storage.set(SUBSCRIPTION_ID_KEY, activeSub.subscriptionId);
        }

        // Obtener información de uso
        try {
          const usageData = await getSubscriptionUsage(activeSub.subscriptionId);
          setUsage(usageData.usage);
        } catch (usageError) {
          console.warn('[SubscriptionContext] Error obteniendo uso:', usageError);
        }
      } else {
        setSubscription(null);
        setUsage(null);
        storage.remove(SUBSCRIPTION_ID_KEY);
      }
    } catch (err) {
      console.error('[SubscriptionContext] Error verificando suscripción:', err);

      // Reintentar antes de dar por fallido
      if (retryCount < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 1000 * (retryCount + 1)));
        return checkSubscription(retryCount + 1);
      }

      setError(err.response?.data?.message || err.message);
      setSubscription(null);
      setUsage(null);
      storage.remove(SUBSCRIPTION_ID_KEY);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  /**
   * Crea una nueva suscripción para la organización
   * @param {string} planId - ID del plan seleccionado
   * @param {boolean} startTrial - Si debe iniciar con trial period (30 días)
   */
  const createSubscription = async (planId, startTrial = true) => {
    try {
      setError(null);
      if (!organization || !organization.organizationId) {
        throw new Error('No se encontró organización');
      }

      const subscriptionData = {
        organizationId: organization.organizationId,
        planId: planId,
        // Datos del usuario para emails transaccionales
        userEmail: user?.username || null,
        userName: organization?.name || user?.username?.split('@')[0] || null,
      };

      // Si se solicita trial, agregar el campo
      if (startTrial) {
        subscriptionData.trialEnabled = true;
      }

      const response = await createSubscriptionAPI(subscriptionData);

      if (response.subscription) {
        setSubscription(response.subscription);

        // Guardar subscriptionId en localStorage
        if (response.subscription.subscriptionId) {
          storage.set(SUBSCRIPTION_ID_KEY, response.subscription.subscriptionId);
        }

        // Obtener información de uso inicial
        try {
          const usageData = await getSubscriptionUsage(response.subscription.subscriptionId);
          setUsage(usageData.usage);
        } catch (usageError) {
          console.warn('[SubscriptionContext] Error obteniendo uso inicial:', usageError);
        }

        return { success: true, subscription: response.subscription };
      }

      return { success: false, error: 'No se recibió la suscripción en la respuesta' };
    } catch (err) {
      console.error('[SubscriptionContext] Error creando suscripción:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  /**
   * Actualiza el estado de la suscripción (refetch)
   */
  const refreshSubscription = async () => {
    await checkSubscription();
  };

  /**
   * Verifica si el usuario tiene una suscripción activa
   */
  const hasActiveSubscription = () => {
    return (
      subscription &&
      (subscription.status === 'active' ||
        subscription.status === 'trial' ||
        subscription.status === 'canceling')
    );
  };

  /**
   * Verifica si está en período de trial
   */
  const isInTrial = () => {
    return subscription?.status === 'trial';
  };

  /**
   * Obtiene el plan actual
   */
  const getCurrentPlan = () => {
    return subscription?.planId || 'FREE';
  };

  /**
   * Verifica si el plan actual incluye una feature específica.
   *
   * La fuente de verdad para el feature gating es `subscription.limits.{xxxEnabled}`
   * (boolean). El campo `subscription.features` es un array de strings de marketing en
   * castellano (ej. 'Pedidos takeaway') y NO debe usarse para gating. Ver seedPlans.js
   * (services/subscriptions) para las claves reales de `limits`.
   *
   * @param {string} featureName — nombre lógico del feature (e.g., 'delivery', 'inventory')
   */
  const hasFeature = (featureName) => {
    if (!subscription) return false;
    // featureName lógico → clave real en subscription.limits (seedPlans.js)
    const flagMap = {
      delivery: 'deliveryEnabled',
      takeaway: 'takeawayEnabled',
      inventory: 'inventoryEnabled',
      recipes: 'recipesEnabled',
      suppliers: 'suppliersEnabled',
      cashRegister: 'cashRegisterEnabled',
      kds: 'kdsEnabled',
      floorPlan: 'floorPlanEnabled',
      tableManagement: 'floorPlanEnabled',
      afipInvoicing: 'afipInvoicingEnabled',
      payments: 'paymentIntegrationsEnabled',
      analytics: 'advancedReportsEnabled',
      advancedAnalytics: 'advancedReportsEnabled',
      dataExport: 'dataExportEnabled',
      crm: 'crmEnabled',
      promotions: 'promotionsEnabled',
      customerApp: 'customerAppEnabled',
      waitingQueue: 'waitingQueueEnabled',
      multiLocation: 'multiRestaurantEnabled',
      multiRestaurant: 'multiRestaurantEnabled',
      whiteLabel: 'whitelabelEnabled',
      api: 'privateAPIEnabled',
      sla: 'slaEnabled',
      dedicatedManager: 'dedicatedSupportEnabled',
      dedicatedSupport: 'dedicatedSupportEnabled',
    };
    const limitKey = flagMap[featureName];
    if (limitKey) return subscription.limits?.[limitKey] === true;
    return false;
  };

  /**
   * Verifica si se alcanzó un límite específico
   * @param {string} limitKey — e.g., 'maxMenuItems', 'maxTables', 'maxUsers', 'maxMonthlyOrders'
   * @returns {{ allowed: boolean, current: number, max: number }}
   */
  const checkLimit = (limitKey) => {
    const max = usage?.limits?.[limitKey];
    const currentMap = {
      maxMenuItems: usage?.usage?.menuItems,
      maxTables: usage?.usage?.tables,
      maxUsers: usage?.usage?.users,
      maxMonthlyOrders: usage?.usage?.orders,
    };
    const current = currentMap[limitKey] || 0;
    if (max === -1 || max === undefined) return { allowed: true, current, max: -1 };
    return { allowed: current < max, current, max };
  };

  /**
   * Returns the minimum plan needed for a given feature
   */
  const getRequiredPlan = (featureName) => {
    const featurePlanMap = {
      modifiers: 'starter',
      kds: 'starter',
      tableManagement: 'starter',
      analytics: 'starter',
      payments: 'starter',
      menuSchedules: 'professional',
      multiLanguageMenu: 'professional',
      delivery: 'professional',
      advancedAnalytics: 'professional',
      expenseTracking: 'professional',
      deliveryPlatforms: 'professional',
      multiLocation: 'business',
      api: 'business',
      whiteLabel: 'enterprise',
      dedicatedManager: 'enterprise',
    };
    return featurePlanMap[featureName] || 'professional';
  };

  const value = {
    subscription,
    usage,
    loading,
    initialized, // Indica si ya se hizo la verificación inicial
    error,
    createSubscription,
    refreshSubscription,
    checkSubscription,
    hasActiveSubscription,
    isInTrial,
    getCurrentPlan,
    hasFeature,
    checkLimit,
    getRequiredPlan,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
